import React from "react";
import { router } from "@inertiajs/react";
import { buildLoginUrl } from "./loginRedirectUrl";

// The server ships `webinar.cta` as the source of truth. This hook layers
// two refinements on top:
//
//   1. Time-window promotion — once `starts_at_iso` passes, a `registered`
//      user should see "Gabung Sekarang" (join) without a reload. When
//      `ended_at_iso` passes, `join` demotes to `view_recording` (if video
//      is present server-side) or `ended` (if not). This avoids the
//      classic stale-CTA bug where a user on the page when the webinar
//      starts still sees "Terdaftar" until they reload.
//
//   2. Client action mapping — each `kind` gets the function the button
//      should call on click. The caller wires `onClick={cta.action}`.
//
// The server's `label`, `variant`, `disabled`, and `hint` are preserved
// verbatim unless the kind promotes/demotes, in which case we pick a
// canonical replacement label.
export default function useWebinarCta(webinar, { authRebrand, onRegister }) {
  const [now, setNow] = React.useState(() => new Date());

  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const startsAt = parseIso(webinar.starts_at_iso);
  const endedAt = parseIso(webinar.ended_at_iso);
  const live =
    startsAt && endedAt
      ? now >= startsAt && now <= endedAt
      : Boolean(webinar.is_live);
  const done = endedAt ? now > endedAt : Boolean(webinar.is_done);
  const hasVideo =
    Boolean(webinar.video_embed_url) || webinar.video_visible === true;

  const serverCta = webinar.cta || {};
  const baseKind = serverCta.kind || "login";
  const kind = promote(baseKind, { live, done, hasVideo });

  const label = labelFor(kind, serverCta.label);
  const variant = variantFor(kind, serverCta.variant);
  const disabled = disabledFor(kind, serverCta.disabled);
  const hint = serverCta.hint;

  const action = actionFor(kind, webinar, {
    authRebrand,
    onRegister,
  });

  return { kind, label, variant, disabled, hint, action };
}

function parseIso(s) {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function promote(kind, { live, done, hasVideo }) {
  // Promote "registered" into the right next state as time passes.
  if (kind === "registered") {
    if (live) return "join";
    if (done && hasVideo) return "view_recording";
    if (done) return "ended";
    return "registered";
  }
  // Demote a stale "join" when the webinar finishes mid-session.
  if (kind === "join" && done) {
    return hasVideo ? "view_recording" : "ended";
  }
  // Demote a stale "register" / "subscribe" / "subscribe_for_special" /
  // "login" surface to "ended" when the webinar ends while the user sits
  // on the page. Without this, a non-registered user could click
  // "Daftar Sekarang" after the webinar has finished — the server would
  // reject the mutation, but better to show the disabled-ended state
  // upfront than surface a confusing "Pendaftaran gagal" toast.
  if (
    done &&
    (kind === "register" ||
      kind === "subscribe" ||
      kind === "subscribe_for_special" ||
      kind === "login")
  ) {
    return "ended";
  }
  return kind;
}

function labelFor(kind, serverLabel) {
  // Prefer the server label when the kind wasn't promoted, so admins can
  // tweak copy without a code change. Canonical fallbacks for promoted
  // kinds follow the legacy wording.
  const fallback = {
    login: "Masuk untuk Daftar",
    register: "Daftar Sekarang",
    registered: "Terdaftar",
    join: "Gabung Sekarang",
    subscribe: "Subscribe untuk Daftar",
    subscribe_for_special: "Khusus Karyawan/Klien",
    view_recording: "Tonton Rekaman",
    claim_certificate: "Klaim Sertifikat",
    pay_certificate: "Bayar Sertifikat",
    ended: "Webinar Telah Selesai",
    quota_full: "Kuota Penuh",
  }[kind];
  return serverLabel || fallback || "Daftar";
}

function variantFor(kind, serverVariant) {
  const fallback = {
    registered: "secondary",
    ended: "muted",
    quota_full: "muted",
    subscribe_for_special: "muted",
  }[kind];
  return fallback || serverVariant || "primary";
}

function disabledFor(kind, serverDisabled) {
  if (
    kind === "registered" ||
    kind === "ended" ||
    kind === "quota_full" ||
    kind === "subscribe_for_special"
  ) {
    return true;
  }
  return Boolean(serverDisabled);
}

function actionFor(kind, webinar, { authRebrand, onRegister }) {
  switch (kind) {
    case "login":
      return () => {
        window.location.href = buildLoginUrl(webinar.slug, authRebrand);
      };
    case "register":
      return () => {
        if (typeof onRegister === "function") onRegister();
      };
    case "join":
      return () => {
        // Full navigation — the controller enqueues the attendance worker
        // and then issues an external redirect to the streaming platform.
        if (webinar.join_url) window.location.href = webinar.join_url;
      };
    case "subscribe":
      return () => {
        router.visit("/memberships");
      };
    case "view_recording":
      // The rebranded page renders <WebinarRecording> inline (between the
      // Hero and Details sections) when the server ships a video_recording
      // payload, so we scroll the user there rather than navigating away.
      // Falls back to a no-op if the section isn't mounted (e.g. legacy
      // page somehow served, or the recording payload was withheld).
      return () => {
        if (typeof document === "undefined") return; // SSR guard
        const el = document.getElementById("webinar-recording");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };
    case "claim_certificate":
    case "pay_certificate":
      // Portal /sertifikat is the source of truth for the webinar cert flow
      // (claim, pay, download). The detail-page CTA routes there; the portal
      // renders the correct action (Klaim vs Bayar vs Unduh) per cert state.
      return () => {
        router.visit("/sertifikat#webinar");
      };
    // registered, ended, quota_full, subscribe_for_special are disabled —
    // the button renders but the action is a no-op.
    default:
      return () => {};
  }
}

// Re-export so callers can use this without importing it separately.
export { buildLoginUrl };
